import type * as Geo from "@/base/geo"
import type { ValuesType } from "utility-types"

import type { SensorName } from "./sensor-configurations"
import { formatSensorState } from "@/base/sensor-formatting"
import type { SelectOption } from "@/theme/form-control/Select"
import type { LabelLength } from "@/base/string-formatting"

export const SENSOR_NAME_TO_STATES = {
  battery: {
    batteryDead: "BD",
    batteryEmergencyShutdown: "BES",
    batteryLow: "BL",
    batteryNormal: "BN",
  },
  depth: {
    depthHigh: "DPH",
    depthLow: "DPL",
    depthNormal: "DPN",
    depthOver: "DPO",
  },
  device: {
    deviceConfigured: "C",
    deviceNoSignal: "NSIG",
    deviceNotConfigured: "NC",
    deviceSleep: "DVS",
    deviceSleepAbort: "DVSA",
    deviceSleepQueued: "DVSQ",
    deviceSleepWake: "DVSW",
  },
  flow: {
    flowFlowing: "FLF",
    flowNotFlowing: "FLN",
    flowOver: "FLOV",
  },
  gps: {
    gpsActive: "A",
    gpsDisabled: "GDSBL",
    gpsError: "GERR",
    gpsInactive: "I",
    gpsOff: "GOFF",
    gpsOnLockedStable: "GST",
    gpsOnLockedUnstable: "GLK",
    gpsOnUnlocked: "GULK",
  },
  hallSwitch: {
    magnetAbsent: "MA",
    magnetPresent: "MP",
  },
  humidity: {
    humidityHigh: "HHI",
    humidityLow: "HLO",
    humidityNormal: "HNO",
  },
  icm: {
    icmActive: "A",
    icmInactive: "I",
  },
  pivotControl: {
    // Mirrors backend.constants.SensorStates CP_* entries. Keep keys and
    // wire values in sync with backend/constants.py — firmware is authoritative.
    cpOff: "CPOF",
    cpIdleDry: "CPID",
    cpIdleWet: "CPIW",
    cpFwdDry: "CPFD",
    cpFwdWet: "CPFW",
    cpRvsDry: "CPRD",
    cpRvsWet: "CPRW",
    cpInitializing: "CPIN",
    cpShutdown: "CPSH",
    cpErrorDisconnected: "CPDC",
    cpWaterTimeout: "CPWT",
    cpSpeedTimeout: "CPST",
    cpActionTimeout: "CPAT",
    cpRevstopTimeout: "CPRT",
    cpEmergencyShutdown: "CPES",
    cpSafetyLost: "CPSL",
    cpRevstopOnWithoutDirection: "CPERD",
    cpForwardSignalLost: "CPFL",
    cpReverseSignalLost: "CPRL",
    cpForwardUnexpectedlyPresent: "CPFU",
    cpReverseUnexpectedlyPresent: "CPRU",
    // CPSUN, not CPSU — "CPST" was already taken by cpSpeedTimeout.
    cpSpeedUnreachable: "CPSUN",
  },
  pressure: {
    pressureHigh: "PHI",
    pressureLow: "PLO",
    pressureOver: "POV",
  },
  pressureSwitch: {
    pressureSwitchHigh: "PHI",
    pressureSwitchLow: "PLO",
  },
  reel: {
    reelExtending: "RE",
    reelRetracting: "RR",
    reelStopped: "RS",
  },
  relay: {
    relayOff: "OFF",
    relayOn: "ON",
  },
  temperature: {
    temperatureHigh: "THI",
    temperatureLow: "TLO",
    temperatureNormal: "TNO",
  },
  vfd: {
    off: "VFDOF",
    onRamping: "VFDRP",
    onStable: "VFDST",
    pidTracking: "VFDPIDT",
    vfdSpeedOverMax: "VFDOV",
    vfdSpeedUnderMin: "VFDUN",
  },
  wheel: {
    wheelFast: "WF",
    wheelSlow: "WL",
    wheelStopped: "WS",
    wheelUnknown: "WUNK",
  },
} as const

// Additional pivot control states (not part of base state)
export const PIVOT_CONTROL_DIRECTION_STATES = {
  clockwise: "PCCW",
  counterclockwise: "PCCCW",
} as const

export const PIVOT_CONTROL_WET_DRY_STATES = {
  wet: "PCWET",
  dry: "PCDRY",
} as const

export const PIVOT_CONTROL_END_GUN_STATES = {
  on: "PCEGON",
  off: "PCEGOFF",
} as const

export type PivotControlDirection = typeof PIVOT_CONTROL_DIRECTION_STATES[keyof typeof PIVOT_CONTROL_DIRECTION_STATES]
export type PivotControlWetDry = typeof PIVOT_CONTROL_WET_DRY_STATES[keyof typeof PIVOT_CONTROL_WET_DRY_STATES]
export type PivotControlEndGun = typeof PIVOT_CONTROL_END_GUN_STATES[keyof typeof PIVOT_CONTROL_END_GUN_STATES]

/**
 * Wire codes the pivot controller emits when it has tripped into an
 * error state and won't accept normal control commands until cleared.
 * Values mirror SensorStates.CP_ERROR_* in backend/constants.py.
 */
export const PIVOT_CONTROLLER_ERROR_STATES = {
  CPDC: "Disconnected from pivot",
  CPWT: "Water timeout",
  CPST: "Speed timeout",
  CPAT: "Action timeout",
  CPRT: "Reverse-stop timeout",
  CPES: "Emergency shutdown",
  CPSL: "Safety circuit lost",
  CPERD: "Reverse-stop on without direction",
  // Direction-feedback mismatch family: a driven command's outcome
  // didn't match the wired i_fwd/i_rvs feedback. The "failed to stop"
  // pair is the more urgent alert (welded contactor / external motion).
  CPFL: "Pivot failed to start or stopped while commanded to run",
  CPRL: "Pivot failed to start or stopped while commanded to run (reverse)",
  CPFU: "Pivot failed to stop (forward signal still present)",
  CPRU: "Pivot failed to stop (reverse signal still present)",
  // A tuning problem rather than a hardware one: the pivot sat at full
  // (or zero) output for five minutes without reaching the commanded
  // speed. Says what to change, since inspecting wiring won't help.
  CPSUN: "Speed target unreachable — lower the target speed or retune",
} as const

export type PivotControllerErrorState = keyof typeof PIVOT_CONTROLLER_ERROR_STATES

/** True when the supplied wire-code state is one of the pivot controller's
 *  error codes (CPDC/CPWT/CPST/CPAT/CPRT/CPES/CPSL/CPERD/CPFL/CPRL/CPFU/
 *  CPRU/CPSUN). */
export function isPivotControllerErrorState(
  state: string | null | undefined,
): state is PivotControllerErrorState {
  if (state == null) return false
  return state in PIVOT_CONTROLLER_ERROR_STATES
}

/** Human-readable label for an error state, falling back to the wire code
 *  for unknown values (firmware may add new codes ahead of UI updates). */
export function getPivotControllerErrorLabel(state: string): string {
  if (isPivotControllerErrorState(state)) {
    return PIVOT_CONTROLLER_ERROR_STATES[state]
  }
  return `Unknown error (${state})`
}
export type AnySensorState = ValuesType<
  (typeof SENSOR_NAME_TO_STATES)[SensorName]
>
type GetStatesOfSensor<S extends SensorName> = S extends S
  ? ValuesType<(typeof SENSOR_NAME_TO_STATES)[S]>
  : never
export type SensorState<
  S extends SensorName = SensorName,
  V extends GetStatesOfSensor<S> = GetStatesOfSensor<S>,
> = S extends S ? (V extends V ? V | "NONE" : never) : never

export const BASE_SENSOR_EVENT_KEYS = [
  "sensorTriggeredUpdate",
  "stateCurrent",
  "stateCurrentInitDt",
  "statePrevious",
  "statePreviousInitDt",
] as const
export const SENSOR_NAME_TO_EVENT_KEYS = {
  battery: [
    ...BASE_SENSOR_EVENT_KEYS,
    "voltageMv",
    "voltageChangeMvPerHour",
    "internalSoc",
  ],
  depth: [
    ...BASE_SENSOR_EVENT_KEYS,
    "measuredM",
  ],
  device: [
    ...BASE_SENSOR_EVENT_KEYS,
    "configurationId",
    "sensorPriority",
    "signalStrength",
    "signalQuality",
    "otaEnabled",
  ],
  flow: [
    ...BASE_SENSOR_EVENT_KEYS,
    "rateLpmAvg",
    "rateLpmMax",
    "rateLpmMin",
    "reportingPeriodEndDt",
    "reportingPeriodStartDt",
    "volumeLInitial",
    "volumeLFinal",
    "volumeLTotal",
  ],
  gps: [...BASE_SENSOR_EVENT_KEYS, "location", "altitude", "nSats", "heading"],
  hallSwitch: [...BASE_SENSOR_EVENT_KEYS],
  humidity: [...BASE_SENSOR_EVENT_KEYS, "relativePercentage"],
  icm: [...BASE_SENSOR_EVENT_KEYS],
  pivotControl: [
    ...BASE_SENSOR_EVENT_KEYS,
    "direction",
    "speedPercent",
    "wetDry",
    "endGun",
  ],
  pressure: [...BASE_SENSOR_EVENT_KEYS, "readingKpa"],
  pressureSwitch: [...BASE_SENSOR_EVENT_KEYS],
  reel: [
    ...BASE_SENSOR_EVENT_KEYS,
    "runDistanceMmCurrent",
    "runDistanceMmMax",
    "runSpeedMmpm",
    "linearSpeedMmHMin",
    "linearSpeedMmHMax",
    "runMagnetCountCurrent",
    "runMagnetCountMax",
    "speedMillirpm",
  ],
  relay: [...BASE_SENSOR_EVENT_KEYS],
  temperature: [...BASE_SENSOR_EVENT_KEYS, "readingCelsius"],
  vfd: [
    ...BASE_SENSOR_EVENT_KEYS,
    "currentPercentage",
    "deviceFunctionCall",
    "pidDerivativeCoeff",
    "pidGainCoeff",
    "pidIntegralCoeff",
    "pidSensorSetpoint",
    "pidMeasurementType",
    "rampSecondsRemaining",
    "rampSecondsTotal",
    "scheduledDeviceAction",
    "startRampPercentage",
    "targetRampPercentage",
    // "deviceFunctionCall",
    // "scheduledDeviceAction"
  ],
  wheel: [...BASE_SENSOR_EVENT_KEYS, "speedMmpm", "milliRpmAvg"],
} as const
type GetKeysOfSensorEvent<S extends SensorName> =
  (typeof SENSOR_NAME_TO_EVENT_KEYS)[S][number]

export type SensorEventKey<S extends SensorName = SensorName> = S extends S
  ? GetKeysOfSensorEvent<S>
  : never
export type GetSensorEventValue<
  S extends SensorName,
  K extends SensorEventKey<S> = SensorEventKey<S>,
> = K extends K
  ? K extends "stateCurrent" | "statePrevious"
    ? SensorState<S>
    : K extends
          | "reportingPeriodEndDt"
          | "reportingPeriodStartDt"
          | "stateCurrentInitDt"
          | "statePreviousInitDt"
      ? string
      : K extends "sensorTriggeredUpdate"
        ? boolean
        : K extends "location"
          ? Geo.PointGeoJson
          : number | null | undefined
  : never
export type SensorEvent<S extends SensorName> = S extends S
  ? {
      [key in SensorEventKey<S>]:
        | GetSensorEventValue<S, key>
        | null
        | undefined
    }
  : never

export interface SensorEvents {
  battery?: SensorEvent<"battery"> | null
  depth?: SensorEvent<"depth"> | null
  device?: SensorEvent<"device"> | null
  flow?: SensorEvent<"flow"> | null
  gps?: SensorEvent<"gps"> | null
  hallSwitch?: SensorEvent<"hallSwitch"> | null
  humidity?: SensorEvent<"humidity"> | null
  icm?: SensorEvent<"icm"> | null
  /**
   * Center pivot controller state (running/stopped, direction, speed, wet/dry, end gun)
   */
  pivotControl?: SensorEvent<"pivotControl"> | null
  /**
   * Default for pumps and reels from FC2 onward
   */
  pressure?: SensorEvent<"pressure"> | null

  /**
   * Default for legacy PC1
   */
  pressureSwitch?: SensorEvent<"pressureSwitch"> | null

  reel?: SensorEvent<"reel"> | null
  relay?: SensorEvent<"relay"> | null
  temperature?: SensorEvent<"temperature"> | null
  vfd?: SensorEvent<"vfd"> | null
  wheel?: SensorEvent<"wheel"> | null
}

const cardinalDirections = [
  "N",
  "NE",
  "E",
  "SE",
  "S",
  "SW",
  "W",
  "NW",
  "N",
] as const

/**
 * Get direction from degrees
 */
export function degreesToCardinalDirection(degrees: number) {
  // Define an array of cardinal directions in order

  // Calculate the index of the cardinal direction based on the degrees
  const index = Math.round(degrees / 45) % 8

  // Return the cardinal direction
  return cardinalDirections[index]
}
export function getStateOptionsForSensor(
  sensorName: SensorName | "" | null | undefined,
  options?: {
    labelLength: LabelLength
  },
): SelectOption[] {
  const labelLength = options?.labelLength ?? "short"
  if (typeof sensorName === "string" && sensorName !== "") {
    const states = { ...SENSOR_NAME_TO_STATES[sensorName] }
    const values = states as typeof states | undefined
    return Object.values(values ?? {}).map((sensorState) => {
      return {
        label: formatSensorState(sensorState, { labelLength }),
        value: sensorState,
      }
    })
  }
  return []
}
